/**
 * CardInsidePreview
 *
 * Renders the inside-right panel of the folded greeting card, where the message
 * prints. The left-edge crease shadow reads as the fold of the card. Always
 * grows to show ALL typed content with no clipping, so what users see in the
 * preview exactly matches what prints on the card.
 *
 * (The handwritten/cursive variant was retired with the 2026-06 folded-card
 * pivot. The `handwritten` prop is accepted but ignored for backward-compat.)
 */

import type { CSSProperties } from 'react';
import type { DesignData } from './CardDesignPreview';
import { getCardFontPreview } from '../../../../shared/src/cardFonts';

interface CardInsidePreviewProps {
  design:         DesignData;
  message:        string;
  placeholder?:   string;
  /** Opening salutation e.g. "Dear Mom," */
  cardGreeting?:  string;
  /** Card closing phrase e.g. "With love," */
  cardClosing?:   string;
  /** From line e.g. "Mom & Dad" */
  cardFromLine?:  string;
  /** The user's chosen handwriting font id, so the preview matches what prints. */
  cardFontId?:    number;
  /** Message font size in px (default 18). Lower it to fit a longer message. */
  fontSize?:      number;
  /** @deprecated Handwritten variant retired; prop ignored */
  handwritten?:   boolean;
  /** @deprecated Signature feature removed; prop ignored */
  signatureUrl?:  string | null;
}

export default function CardInsidePreview({
  design,
  message,
  placeholder = 'Your heartfelt message will appear here, beautifully printed on the inside of the card.',
  cardGreeting,
  cardClosing,
  cardFromLine,
  cardFontId,
  fontSize = 18,
}: CardInsidePreviewProps) {
  const isEmpty   = !message.trim();
  const lineColor = design.accent_color;
  // Match the on-screen text to the handwriting style the user picked, so the
  // preview reads like the printed card instead of a typed serif letter.
  const { family: msgFont, weight: msgWeight } = getCardFontPreview(cardFontId);

  // The inside-right panel of the folded card: branded accent bar across the
  // top and a crease shadow down the left edge (the fold).
  const outerStyle: CSSProperties = {
    position:        'relative',
    backgroundColor: '#FEFEFE',
    borderRadius:    12,
    overflow:        'hidden',
    boxShadow:       '0 2px 20px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(0,0,0,0.05)',
    display:         'flex',
    flexDirection:   'column',
    minHeight:       120,
    padding:         '16px 22px 14px 26px',
    fontFamily:      msgFont,
  };

  // Crease shadow down the left edge (the fold of the card).
  const foldStyle: CSSProperties = {
    position:      'absolute',
    top:           0,
    left:          0,
    width:         8,
    bottom:        0,
    background:    'linear-gradient(to right, rgba(0,0,0,0.06), transparent)',
    pointerEvents: 'none',
  };

  // Accent bar across the top.
  const topLineStyle: CSSProperties = {
    position:        'absolute',
    top:             0,
    left:            8,
    right:           0,
    height:          3,
    backgroundColor: lineColor,
    opacity:         0.7,
  };

  const messageStyle: CSSProperties = {
    fontSize:      fontSize,
    lineHeight:    1.45,
    color:         isEmpty ? '#B0A99F' : '#1A1410',
    fontStyle:     isEmpty ? 'italic' : 'normal',
    fontWeight:    msgWeight,
    fontFamily:    msgFont,
    whiteSpace:    'pre-wrap',
    wordBreak:     'break-word',
    margin:        '8px 0 12px 0',
    letterSpacing: 'normal',
  };

  return (
    <div style={outerStyle}>
      <div style={foldStyle} aria-hidden />
      <div style={topLineStyle} aria-hidden />

      {cardGreeting && (
        <p style={{
          ...messageStyle,
          color:     '#1A1410',
          fontStyle: 'normal',
          margin:    '8px 0 2px 0',
        }}>
          {cardGreeting}
        </p>
      )}
      <p style={{ ...messageStyle, marginTop: cardGreeting ? 4 : messageStyle.margin as string }}>
        {isEmpty ? placeholder : message}
      </p>

      {(cardClosing || cardFromLine) && (
        <div style={{
          marginTop:  10,
          fontFamily: msgFont,
          fontSize:   fontSize,
          fontWeight: msgWeight,
          color:      '#1A1410',
          fontStyle:  'normal',
          lineHeight: 1.4,
        }}>
          {cardClosing  && <div>{cardClosing}</div>}
          {cardFromLine && <div>{cardFromLine}</div>}
        </div>
      )}

      <div style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'flex-end',
        gap:            6,
        marginTop:      8,
      }}>
        <p style={{
          fontSize:      8,
          fontFamily:    '"Inter", Arial, sans-serif',
          color:         '#B0AAA4',
          letterSpacing: '0.03em',
        }}>
          Sent with MomentSent
        </p>
      </div>
    </div>
  );
}
